import { ClicktermInlineHandle } from '../models';

interface PendingInlineRegistryEntry {
  container: HTMLElement;
  state: 'pending';
  token: symbol;
}

interface ActiveInlineRegistryEntry {
  container: HTMLElement;
  state: 'active';
  handle: ClicktermInlineHandle;
}

export type InlineRegistryEntry = PendingInlineRegistryEntry | ActiveInlineRegistryEntry;

class InlineRegistry {
  private readonly entries = new Map<string, InlineRegistryEntry>();

  hasContainer(containerId: string): boolean {
    return Boolean(this.getCurrentEntry(containerId));
  }

  reserve(containerId: string, container: HTMLElement): symbol {
    const token = Symbol(containerId);
    this.entries.set(containerId, {
      container,
      state: 'pending',
      token,
    });
    return token;
  }

  register(
    containerId: string,
    container: HTMLElement,
    handle: ClicktermInlineHandle,
    reservation?: symbol,
  ): boolean {
    if (reservation) {
      const entry = this.getCurrentEntry(containerId);
      if (!entry || entry.state !== 'pending' || entry.token !== reservation || entry.container !== container) {
        return false;
      }
    }

    this.entries.set(containerId, {
      container,
      state: 'active',
      handle,
    });
    return true;
  }

  unregister(containerId: string, reservation?: symbol): void {
    if (reservation) {
      const entry = this.entries.get(containerId);
      if (!entry || entry.state !== 'pending' || entry.token !== reservation) {
        return;
      }
    }
    this.entries.delete(containerId);
  }

  getEntry(containerId: string): InlineRegistryEntry | undefined {
    return this.entries.get(containerId);
  }

  getCurrentEntry(containerId: string): InlineRegistryEntry | undefined {
    const entry = this.entries.get(containerId);
    if (!entry) {
      return undefined;
    }
    if (!this.isEntryCurrent(containerId, entry)) {
      this.destroyEntry(containerId, entry);
      return undefined;
    }
    return entry;
  }

  isCurrentContainer(containerId: string, container: HTMLElement): boolean {
    const entry = this.getCurrentEntry(containerId);
    return entry ? entry.container === container : false;
  }

  getHandle(containerId: string): ClicktermInlineHandle | undefined {
    const entry = this.getCurrentEntry(containerId);
    return entry?.state === 'active' ? entry.handle : undefined;
  }

  getContainerIds(): string[] {
    this.pruneStaleEntries();
    return Array.from(this.entries.entries())
      .filter(([, entry]) => entry.state === 'active')
      .map(([containerId]) => containerId);
  }

  private pruneStaleEntries(): void {
    this.entries.forEach((entry, containerId) => {
      if (!this.isEntryCurrent(containerId, entry)) {
        this.destroyEntry(containerId, entry);
      }
    });
  }

  private destroyEntry(containerId: string, entry: InlineRegistryEntry): void {
    if (entry.state === 'active') {
      entry.handle.destroy?.();
    }
    if (this.entries.get(containerId) === entry) {
      this.entries.delete(containerId);
    }
  }

  private isEntryCurrent(
    containerId: string,
    entry: InlineRegistryEntry,
    container?: HTMLElement,
  ): boolean {
    if (!entry.container.isConnected) {
      return false;
    }

    const currentContainer = entry.container.ownerDocument.getElementById(containerId);
    if (currentContainer !== entry.container) {
      return false;
    }

    if (container && currentContainer !== container) {
      return false;
    }

    return true;
  }
}

export const inlineRegistry = new InlineRegistry();
